import type { FormEvent, ReactElement } from 'react';
import { useState } from 'react';
import { formatApiErrorMessage } from '@fetmode/api-client';
import { RegisterAsModelLink, RegisterLegalConsent, useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

export function AcceptAlphaInvitePage(): ReactElement {
  const { api, applySession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token') ?? '';

  const [token, setToken] = useState(tokenFromUrl);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!acceptedTerms) {
      setError(t('common.auth.termsRequired'));
      return;
    }

    setSubmitting(true);

    try {
      const result = await api.acceptAlphaInvite({
        token: token.trim(),
        username: username.trim().toLowerCase(),
        password,
      });
      applySession(result.session);
      void navigate('/onboarding/experience', { replace: true });
    } catch (err: unknown) {
      setError(
        formatApiErrorMessage(err, 'Unable to accept invite. The link may have expired.'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section style={{ maxWidth: '480px' }}>
      <h1 style={{ fontFamily: 'var(--fm-font-heading)', marginTop: 0 }}>Join closed alpha</h1>
      <p style={{ color: 'var(--fm-color-text-muted)' }}>
        You were invited to test FetMode. Create your account to start the guided mission.
      </p>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', marginBottom: '1rem' }}>
          <span>Invite token</span>
          <input
            type="text"
            required
            value={token}
            onChange={(event) => {
              setToken(event.target.value);
            }}
            className="fm-input"
            autoComplete="off"
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', marginBottom: '1rem' }}>
          <span>{t('common.auth.username')}</span>
          <input
            type="text"
            required
            minLength={3}
            maxLength={30}
            pattern="[a-zA-Z0-9_]+"
            title="3–30 characters: letters, numbers, underscores"
            value={username}
            onChange={(event) => {
              setUsername(event.target.value);
            }}
            className="fm-input"
            autoComplete="username"
          />
          <span style={{ color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
            3–30 characters · letters, numbers, underscores only (no spaces)
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', marginBottom: '1rem' }}>
          <span>{t('common.auth.password')}</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
            className="fm-input"
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', marginBottom: '1rem' }}>
          <span>Confirm password</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={confirmPassword}
            onChange={(event) => {
              setConfirmPassword(event.target.value);
            }}
            className="fm-input"
          />
        </label>
        <RegisterLegalConsent checked={acceptedTerms} onCheckedChange={setAcceptedTerms} />
        {error ? (
          <p role="alert" className="fm-error">
            {error}
          </p>
        ) : null}
        <Button label="Create alpha account" type="submit" disabled={submitting} />
      </form>
      <p style={{ marginTop: '1rem' }}>
        <Link to="/login">{t('common.nav.signIn')}</Link>
      </p>
      <RegisterAsModelLink />
    </section>
  );
}
